import { createContext, useContext, useMemo, useState } from 'react';
import { format, parseISO, startOfMonth } from 'date-fns';
import { Calendar as CalendarIcon, CalendarDays, CalendarRange, Loader2 } from 'lucide-react';
import { DayButton, type DayButtonProps } from 'react-day-picker';
import { DateCalendar } from '@/components/common/DateCalendar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { WEEK_STARTS_ON } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import { CALENDAR_POPOVER_CLASS } from '@/styles/floatingPanel';
import { formatDate } from '@/utils/date';
import { formatCount } from '@/utils/number';
import type { MapOn } from '../constants/moTravel.constants';
import { formatFullDay, formatShortDay } from '../utils/dayLabels';
import type { DayRoute } from '../utils/dayRoutes';
import {
  formatPeriodLabel,
  getPeriodRange,
  isDayInPeriod,
  isMultiDayPeriod,
} from '../utils/moTravelPeriod';

const PERIOD_CLASS_NAMES = {
  period: 'bg-primary/10',
  periodStart: 'rounded-l-lg',
  periodEnd: 'rounded-r-lg',
};

const SUMMARY_DOT_LIMIT = 4;
const NO_ROUTES: DayRoute[] = [];
const DAILY_TRIGGER_FORMAT = 'MMM dd, yyyy';

const DayColorContext = createContext<ReadonlyMap<string, string>>(new Map());

function MarkedDayButton({ className, children, ...dayButtonProps }: DayButtonProps) {
  const colorByDate = useContext(DayColorContext);
  const markColor = colorByDate.get(formatDate(dayButtonProps.day.date));
  const shouldShowMark =
    markColor !== undefined &&
    !dayButtonProps.modifiers.selected &&
    !dayButtonProps.modifiers.outside;

  return (
    <DayButton {...dayButtonProps} className={cn(className, 'relative')}>
      {children}
      {shouldShowMark && (
        <>
          <span
            className="absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full"
            style={{ background: markColor }}
            aria-hidden
          />
          <span className="sr-only">, journey recorded</span>
        </>
      )}
    </DayButton>
  );
}

function formatTriggerLabel(date: string, mapOn: MapOn, selectedDay: string | null): string {
  if (selectedDay) return formatFullDay(selectedDay);
  if (mapOn === 'DAILY') return format(parseISO(date), DAILY_TRIGGER_FORMAT);
  return formatPeriodLabel(date, mapOn);
}

interface SummaryDot {
  date: string;
  color: string;
}

interface PeriodSummary {
  title: string;
  detail: string;
  dots: SummaryDot[];
}

const toSummaryDot = (route: DayRoute): SummaryDot => ({
  date: route.day.date,
  color: route.color,
});

interface PeriodSummaryInput {
  date: string;
  mapOn: MapOn;
  routes: DayRoute[];
  selectedDay: string | null;
  isLoading: boolean;
}

function summarizeSelectedDay({
  routes,
  selectedDay,
  isLoading,
}: PeriodSummaryInput & { selectedDay: string }): PeriodSummary {
  const title = formatShortDay(selectedDay);
  const dayIndex = routes.findIndex((route) => route.day.date === selectedDay);
  const selectedRoute = routes[dayIndex];

  if (selectedRoute) {
    return {
      title,
      detail: `Day ${dayIndex + 1} of ${routes.length}`,
      dots: [toSummaryDot(selectedRoute)],
    };
  }
  if (isLoading) return { title, detail: 'Loading journey', dots: [] };
  return { title, detail: 'No journey on this day', dots: [] };
}

function summarizePeriod(summaryInput: PeriodSummaryInput): PeriodSummary {
  const { date, mapOn, routes, selectedDay, isLoading } = summaryInput;
  if (selectedDay) return summarizeSelectedDay({ ...summaryInput, selectedDay });

  if (isLoading) {
    return { title: 'Loading working days', detail: formatPeriodLabel(date, mapOn), dots: [] };
  }
  if (routes.length === 0) {
    return {
      title: mapOn === 'WEEKLY' ? 'No journey this week' : 'No journey this month',
      detail: formatPeriodLabel(date, mapOn),
      dots: [],
    };
  }
  return {
    title: formatCount(routes.length, 'working day'),
    detail: 'Pick a date to see one day',
    dots: routes.slice(0, SUMMARY_DOT_LIMIT).map(toSummaryDot),
  };
}

interface PeriodFooterIconProps {
  dots: SummaryDot[];
  isLoading: boolean;
}

function PeriodFooterIcon({ dots, isLoading }: PeriodFooterIconProps) {
  if (dots.length > 0) {
    const hasSeveralDots = dots.length > 1;
    return (
      <span className={cn('grid gap-0.5', hasSeveralDots && 'grid-cols-2')} aria-hidden>
        {dots.map((dot) => (
          <span
            key={dot.date}
            className={cn('rounded-full', hasSeveralDots ? 'size-2' : 'size-2.5')}
            style={{ background: dot.color }}
          />
        ))}
      </span>
    );
  }
  if (isLoading) {
    return (
      <Loader2 className="size-4 text-muted-foreground motion-safe:animate-spin" aria-hidden />
    );
  }
  return <CalendarDays className="size-4 text-muted-foreground" aria-hidden />;
}

interface PeriodFooterProps extends PeriodSummaryInput {
  onDayClear: () => void;
}

function PeriodFooter({ onDayClear, ...summaryInput }: PeriodFooterProps) {
  const { title, detail, dots } = summarizePeriod(summaryInput);

  return (
    <div className="flex items-center gap-2.5 border-t bg-muted/40 px-3 py-2.5">
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-background ring-1 ring-border">
        <PeriodFooterIcon dots={dots} isLoading={summaryInput.isLoading} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col" aria-live="polite">
        <span className="truncate text-xs font-medium text-foreground">{title}</span>
        <span className="truncate text-2xs text-muted-foreground">{detail}</span>
      </div>
      {summaryInput.selectedDay !== null && (
        <Button variant="outline" size="sm" className="shrink-0 text-xs" onClick={onDayClear}>
          <CalendarRange aria-hidden />
          All days
        </Button>
      )}
    </div>
  );
}

export interface PeriodJourney {
  routes: DayRoute[];
  selectedDay: string | null;
  isLoading: boolean;
  onDayInPeriodPick: (day: string) => void;
  onDayClear: () => void;
}

interface MoPeriodDatePickerProps {
  date: string;
  mapOn: MapOn;
  maxDate: string;
  journey?: PeriodJourney | undefined;
  onDateChange: (date: string) => void;
  className?: string;
}

export function MoPeriodDatePicker({
  date,
  mapOn,
  maxDate,
  journey,
  onDateChange,
  className,
}: MoPeriodDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const routes = journey?.routes ?? NO_ROUTES;
  const selectedDay = journey?.selectedDay ?? null;
  const colorByDate = useMemo(
    () => new Map(routes.map((route) => [route.day.date, route.color])),
    [routes],
  );
  const periodRange = getPeriodRange(date, mapOn);
  const isMultiDay = isMultiDayPeriod(mapOn);
  const periodStart = parseISO(periodRange.from);
  const periodEnd = parseISO(periodRange.to);
  const shownDate = parseISO(selectedDay ?? date);
  const lastSelectableDate = parseISO(maxDate);
  const selectedDayColor = selectedDay ? colorByDate.get(selectedDay) : undefined;
  const triggerLabel = formatTriggerLabel(date, mapOn, selectedDay);

  function handleMonthChange(month: Date) {
    if (mapOn !== 'MONTHLY') return;
    const firstDayOfMonth = formatDate(startOfMonth(month));
    if (!isDayInPeriod(periodRange, firstDayOfMonth)) onDateChange(firstDayOfMonth);
  }

  function handleDayPick(pickedDate: Date) {
    const pickedDay = formatDate(pickedDate);
    if (journey && isMultiDay && isDayInPeriod(periodRange, pickedDay)) {
      journey.onDayInPeriodPick(pickedDay);
      return;
    }
    onDateChange(pickedDay);
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        aria-label={`Change period, ${triggerLabel}`}
        className={cn(
          FIELD_CONTROL_CLASS,
          'flex h-10 cursor-pointer items-center gap-2 border px-3 outline-none select-none sm:h-9',
          className,
        )}
      >
        {selectedDayColor ? (
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: selectedDayColor }}
            aria-hidden
          />
        ) : (
          <CalendarIcon className="size-4 shrink-0 text-brand-link/70" aria-hidden />
        )}
        <span className="truncate text-sm font-medium">{triggerLabel}</span>
      </PopoverTrigger>
      <PopoverContent className={cn(CALENDAR_POPOVER_CLASS, 'gap-0 overflow-hidden')} align="end">
        <DayColorContext.Provider value={colorByDate}>
          <DateCalendar
            mode="single"
            required
            onMonthChange={handleMonthChange}
            weekStartsOn={WEEK_STARTS_ON}
            defaultMonth={shownDate}
            endMonth={lastSelectableDate}
            selected={shownDate}
            onSelect={handleDayPick}
            disabled={{ after: lastSelectableDate }}
            {...(isMultiDay && {
              modifiers: { period: { from: periodStart, to: periodEnd }, periodStart, periodEnd },
              modifiersClassNames: PERIOD_CLASS_NAMES,
            })}
            components={{ DayButton: MarkedDayButton }}
          />
        </DayColorContext.Provider>
        {isMultiDay && journey && (
          <PeriodFooter
            date={date}
            mapOn={mapOn}
            routes={journey.routes}
            selectedDay={journey.selectedDay}
            isLoading={journey.isLoading}
            onDayClear={journey.onDayClear}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
